import { motion } from 'framer-motion';
import {
  Sparkles,
  Brain,
  Lightbulb,
  TrendingUp,
  Target,
  Zap,
  CheckCircle,
  ArrowRight,
  MessageSquare,
  FileText,
  BarChart3,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function AIInsightsPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const aiFeatures = [
    {
      icon: Lightbulb,
      title: 'Vision & Mission Generation',
      description:
        'Get AI-powered suggestions for compelling vision and mission statements based on your industry, goals, and company culture.',
      example:
        'Explore different ways to express your purpose, then refine the statement that fits your company.',
    },
    {
      icon: Target,
      title: 'Strategic Objectives',
      description:
        'AI recommends specific, measurable objectives aligned with your vision and market position.',
      example:
        'Suggests SMART objectives like "Increase market share by 15% in key segments within 18 months."',
    },
    {
      icon: Brain,
      title: 'Core Values Drafts',
      description:
        'Explore draft values based on your company purpose and culture, then choose the principles your team can put into practice.',
      example:
        'Turn an idea such as customer focus into a clear value and the behaviors that support it.',
    },
    {
      icon: TrendingUp,
      title: 'Explore your market context',
      description:
        'Use the market and competitor information you provide to explore questions, risks and potential opportunities.',
      example:
        'Compare possible customer segments and identify assumptions to validate with your own research.',
    },
    {
      icon: FileText,
      title: 'Strategy Recommendations',
      description:
        'AI suggests strategic initiatives and competitive approaches tailored to your specific situation.',
      example:
        'Recommends differentiation strategies based on your unique strengths and market gaps.',
    },
    {
      icon: BarChart3,
      title: 'Turn priorities into next steps',
      description:
        'Break a chosen strategy into draft projects, tasks and responsibilities your team can refine.',
      example:
        'Start with a proposed work plan, then adjust scope, ownership and timing to suit your resources.',
    },
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Start with something useful',
      description:
        'Get a first draft to react to, so you can spend your time refining the direction.',
    },
    {
      icon: CheckCircle,
      title: 'Explore more than one approach',
      description:
        'Compare suggested options and test them against the strengths and constraints of your business.',
    },
    {
      icon: MessageSquare,
      title: 'Guidance in the flow',
      description:
        'Keep useful prompts and suggestions alongside the strategic decision you are working on.',
    },
    {
      icon: Brain,
      title: 'Your judgment stays central',
      description: 'Review and edit every suggestion before it becomes part of your plan.',
    },
  ];

  const useCases = [
    {
      title: 'Startup Founder',
      challenge: "Need to create a strategic plan quickly but don't have strategy experience",
      solution:
        'Use company context to draft a direction, explore priorities and decide what deserves attention first.',
      result: 'A first draft you can review and build on',
    },
    {
      title: 'CEO of Growing Company',
      challenge:
        'Existing strategy feels outdated; need fresh perspectives on market opportunities',
      solution:
        'Compare possible strategic directions using your company information and the market context you provide.',
      result: 'A shortlist of options to investigate',
    },
    {
      title: 'Strategy Team',
      challenge:
        'Conducting annual strategy review; need comprehensive analysis across multiple frameworks',
      solution:
        'Bring your assessment notes together, explore the implications and prepare focused questions for your team.',
      result: 'A clearer basis for the next strategy discussion',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'AI Insights' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Sparkles className="w-4 h-4 mr-2" />
            AI guidance with Pro Activation
          </span>
        }
        title="A better starting point for every strategic decision."
        subtitle="Turn your company context into draft statements, strategic options and practical next steps. AI helps you explore the possibilities. You choose the direction."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Build with AI guidance
        </Link>
      </PageHero>

      {/* AI Features */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Useful help at every stage of the plan.</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bring your vision, industry notes and priorities into the conversation. Get draft
              ideas you can question, edit and connect to your plan.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {aiFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white border border-gray-200 rounded-xl p-6 hover:border-purple-300 hover:shadow-sm transition-all"
              >
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                  <p className="text-sm text-gray-700">
                    <strong>Example:</strong> {feature.example}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Your context in. Ideas to work with.</h2>
            <p className="text-xl text-gray-600">Simple, transparent, and always in your control</p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '1',
                  title: 'You Provide Context',
                  description:
                    'Share information about your company, industry, goals, and current situation.',
                },
                {
                  step: '2',
                  title: 'AI Analyzes',
                  description:
                    'AI uses the context you provide to draft ideas and explore the strategic questions in front of you.',
                },
                {
                  step: '3',
                  title: 'Get Suggestions',
                  description:
                    'Receive tailored recommendations, insights, and content suggestions specific to your situation.',
                },
                {
                  step: '4',
                  title: 'You Decide',
                  description:
                    "Review, edit, and approve AI suggestions. You're always in control of your final strategy.",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card flex items-start space-x-6 bg-white p-6 rounded-xl shadow-sm"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              Spend more time deciding. Less time facing a blank page.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Ideas for the way you work.</h2>
            <p className="text-xl text-gray-600">
              Ways to use AI suggestions in your own planning process
            </p>
          </motion.div>

          <div className="space-y-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-8 rounded-xl shadow-sm"
              >
                <h3 className="text-2xl font-bold mb-4 text-purple-600">{useCase.title}</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Challenge</h4>
                    <p className="text-gray-600 text-sm">{useCase.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">How you could use it</h4>
                    <p className="text-gray-600 text-sm">{useCase.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">What you take forward</h4>
                    <p className="text-green-600 font-semibold text-sm">{useCase.result}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Privacy */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="public-card max-w-4xl mx-auto bg-blue-50 p-8 rounded-xl border border-blue-200">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 text-center">
                AI brings the options. You bring the judgment.
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    Keep your context together
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Bring the information you choose to share into one planning workspace, so
                    suggestions relate to the business you are building.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    Make every draft your own
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Edit the language, challenge assumptions and refine the ideas before they become
                    part of your plan.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    You Control AI
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Use AI suggestions when helpful, ignore them when not. You're always in charge.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    Connect decisions to delivery
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Take the direction you choose into projects and tasks, keeping your next steps
                    linked to the strategy.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="public-section public-cta py-16 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Bring your next strategic question.
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Start with your company context. Explore the options, shape the direction and turn the
              ideas you choose into practical work.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Started <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
